import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n';
import { translateCategory } from '../lib/categories';
import { isSafeMode } from '../lib/safeMode';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Upload as UploadIcon, Film, Plus, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function UploadPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const safeMode = isSafeMode();
    const { t, locale, builtin } = useI18n();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [categories, setCategories] = useState(['Vlogs']);
    const [category, setCategory] = useState('Entreteniment');
    const [isShort, setIsShort] = useState(false);
    const [isPremium, setIsPremium] = useState(false);
    const [isAdult, setIsAdult] = useState(false);
    const [adultCategory, setAdultCategory] = useState('');
    const [selectedCats, setSelectedCats] = useState(['Entreteniment']);
    const [selectedAdultCats, setSelectedAdultCats] = useState([]);
    const [adultCategories, setAdultCategories] = useState([]);
    const [price, setPrice] = useState('0.99');
    const [file, setFile] = useState(null);
    const [thumb, setThumb] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploadKind, setUploadKind] = useState('video');
    const [series, setSeries] = useState([]);
    const [myVideos, setMyVideos] = useState([]);
    const [selectedSeries, setSelectedSeries] = useState('');
    const [episodeNumber, setEpisodeNumber] = useState('1');
    const [trailerTarget, setTrailerTarget] = useState('');
    const [newSeriesTitle, setNewSeriesTitle] = useState('');
    const [newSeriesAdult, setNewSeriesAdult] = useState(false);
    const [newSeriesAdultCategory, setNewSeriesAdultCategory] = useState('');
    const [newSeriesPremium, setNewSeriesPremium] = useState(false);
    const [newSeriesPrice, setNewSeriesPrice] = useState('0.99');
    const [creatingSeries, setCreatingSeries] = useState(false);
    const [filePreview, setFilePreview] = useState(null);
    const [thumbPreview, setThumbPreview] = useState(null);
    const [contentLanguage, setContentLanguage] = useState((locale || 'ca').split('-')[0]);
    const languageOptions = [
        ...Object.keys(builtin || {}).map((code) => ({ value: code, label: t(`lang.${code}`) })),
        { value: 'other', label: t('lang.other') },
    ];

    useEffect(() => {
        if (loading) return;
        if (!user) navigate('/entrar');

        (async () => {
            try {
                const [response, seriesResponse, videosResponse] = await Promise.all([
                    api.get('/categories'),
                    api.get('/series/mine'),
                    api.get('/users/me/videos'),
                ]);
                setCategories(response.data.categories || ['Entreteniment']);
                setAdultCategories(response.data.adult_categories || []);
                setSeries(seriesResponse.data.items || []);
                setMyVideos(videosResponse.data.items || []);
            } catch {
                // Keep the fallback category when the API is unavailable.
            }
        })();
    }, [loading, navigate, user]);

    useEffect(() => {
        if (!file) { setFilePreview(null); return undefined; }
        const url = URL.createObjectURL(file);
        setFilePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    useEffect(() => {
        if (!thumb) { setThumbPreview(null); return undefined; }
        const url = URL.createObjectURL(thumb);
        setThumbPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [thumb]);

    const createSeries = async () => {
        if (!newSeriesTitle.trim()) { toast.error(t('upload.series_title_error')); return; }
        if (newSeriesAdult && !newSeriesAdultCategory) { toast.error(t('upload.series_adult_category_error')); return; }
        setCreatingSeries(true);
        try {
            const response = await api.post('/series/create', {
                title: newSeriesTitle.trim(),
                description,
                category,
                is_adult: newSeriesAdult && !safeMode,
                adult_category: newSeriesAdult ? newSeriesAdultCategory : null,
                is_premium: newSeriesPremium,
                default_episode_price: newSeriesPremium ? Number(newSeriesPrice) : null,
                series_price: newSeriesPremium ? Number(newSeriesPrice) : null,
            });
            const created = response.data.series;
            setSeries((items) => [created, ...items]);
            setSelectedSeries(created.series_id);
            setNewSeriesTitle('');
            toast.success(t('upload.series_created'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('upload.series_create_error'));
        } finally {
            setCreatingSeries(false);
        }
    };

    const toggleCategory = (c) => {
        setSelectedCats((cur) => {
            if (cur.includes(c)) return cur.length > 1 ? cur.filter((x) => x !== c) : cur;
            const next = [...cur, c];
            setCategory(next[0]);
            return next;
        });
    };

    const toggleAdultCategory = (c) => {
        setSelectedAdultCats((cur) => {
            if (cur.includes(c)) return cur.length > 1 ? cur.filter((x) => x !== c) : cur;
            const next = [...cur, c];
            setAdultCategory(next[0]);
            return next;
        });
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!file) { toast.error(t('upload.err_file')); return; }
        if (!title.trim()) { toast.error(t('upload.err_title')); return; }
        if (uploadKind === 'episode' && !selectedSeries) { toast.error(t('upload.series_select_error')); return; }
        if (uploadKind === 'trailer' && !trailerTarget) { toast.error(t('upload.trailer_target_error')); return; }
        if (isPremium && (!Number.isFinite(Number(price)) || Number(price) < 0.5)) {
            toast.error(t('upload.price_error'));
            return;
        }

        const fd = new FormData();
        fd.append('file', file);
        if (thumb) fd.append('thumbnail', thumb);
        fd.append('title', title);
        fd.append('description', description);
        fd.append('category', selectedCats[0] || category);
        fd.append('categories', JSON.stringify(selectedCats.length ? selectedCats : [category]));
        fd.append('is_short', String(uploadKind === 'trailer' ? true : isShort));
        if (uploadKind !== 'trailer') fd.append('is_premium', String(isPremium));
        if (uploadKind !== 'episode') fd.append('is_adult', String(uploadKind === 'trailer' || safeMode ? false : isAdult));
        if (isAdult && !safeMode && uploadKind !== 'episode' && uploadKind !== 'trailer') {
            if (!selectedAdultCats.length && !adultCategory) { toast.error(t('upload.adult_category_error')); setBusy(false); return; }
            fd.append('adult_category', String(selectedAdultCats[0] || adultCategory));
            fd.append('adult_categories', JSON.stringify(selectedAdultCats.length ? selectedAdultCats : [adultCategory]));
        }
        if (isPremium) fd.append('price', String(price));
        if (uploadKind === 'episode') fd.append('episode_number', episodeNumber);
        fd.append('content_language', contentLanguage || 'ca');
        if (uploadKind === 'trailer') {
            const [targetType, targetId] = trailerTarget.split(':');
            fd.append(targetType === 'video' ? 'trailer_for_video_id' : 'trailer_for_series_id', targetId);
        }
        setBusy(true);
        setProgress(0);
        try {
            const endpoint = uploadKind === 'episode' ? `/series/${selectedSeries}/upload-episode` : '/videos/upload';
            const r = await api.post(endpoint, fd, {
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(t('upload.success'));
            navigate(`/video/${r.data.video_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('common.error'));
        } finally { setBusy(false); }
    };

    if (!user) return null;
    return (
        <main className="mx-auto w-full max-w-4xl px-3 sm:px-5 lg:px-8 py-6 md:py-10 pb-24 md:pb-12" data-testid="upload-page">
            <h1 className="font-display text-2xl md:text-3xl font-semibold mb-5">{t('upload.title_h')}</h1>
            <Card className="p-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                <form onSubmit={onSubmit} className="space-y-4">
                    <section className="space-y-3 rounded-lg border border-[color:var(--alb-hairline)] p-4">
                        <div>
                            <Label>{t('upload.kind')}</Label>
                            <Select value={uploadKind} onValueChange={(value) => { setUploadKind(value); if (value === 'trailer') { setIsPremium(false); setIsShort(true); setIsAdult(false); } else if (value === 'episode') { const target = series.find((item) => item.series_id === selectedSeries); setIsPremium(Boolean(target?.is_premium)); } else { setIsPremium(false); } }}>
                                <SelectTrigger data-testid="upload-kind-select" className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="video">{t('upload.kind_video')}</SelectItem>
                                    <SelectItem value="episode">{t('upload.kind_episode')}</SelectItem>
                                    <SelectItem value="trailer">{t('upload.kind_trailer')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {uploadKind === 'episode' && (
                            <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                                <div><Label>{t('upload.series')}</Label><Select value={selectedSeries} onValueChange={(value) => { setSelectedSeries(value); const target = series.find((item) => item.series_id === value); setIsPremium(Boolean(target?.is_premium)); }}><SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue placeholder={t('upload.series_select')} /></SelectTrigger><SelectContent>{series.map((item) => <SelectItem key={item.series_id} value={item.series_id}>{item.title}</SelectItem>)}</SelectContent></Select></div>
                                <div><Label htmlFor="episode-number">{t('upload.episode')}</Label><Input id="episode-number" type="number" min="1" value={episodeNumber} onChange={(event) => setEpisodeNumber(event.target.value)} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                                <div className="sm:col-span-2 grid gap-2 sm:grid-cols-[1fr_auto]">
                                    <Input value={newSeriesTitle} onChange={(event) => setNewSeriesTitle(event.target.value)} placeholder={t('upload.series_new_title')} className="bg-[color:var(--alb-ink-2)]" />
                                    <Button type="button" onClick={createSeries} disabled={creatingSeries} variant="outline"><Plus className="mr-1 h-4 w-4" /> {t('upload.series_create')}</Button>
                                </div>
                                <div className="sm:col-span-2 grid gap-2 sm:grid-cols-2">
                                    {!safeMode && <Label className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] p-3">{t('upload.series_adult')} <Switch checked={newSeriesAdult} disabled={user.age < 18} onCheckedChange={setNewSeriesAdult} /></Label>}
                                    <Label className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] p-3">{t('upload.series_premium')} <Switch checked={newSeriesPremium} onCheckedChange={setNewSeriesPremium} /></Label>
                                </div>
                                {!safeMode && newSeriesAdult && <div className="sm:col-span-2"><Label>{t('upload.series_adult_category')}</Label><Select value={newSeriesAdultCategory} onValueChange={setNewSeriesAdultCategory}><SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue /></SelectTrigger><SelectContent>{adultCategories.map((item) => <SelectItem key={item} value={item}>{translateCategory(item, locale)}</SelectItem>)}</SelectContent></Select></div>}
                                {newSeriesPremium && <div className="sm:col-span-2"><Label>{t('upload.series_price')}</Label><Input type="number" min="0.5" step="0.01" value={newSeriesPrice} onChange={(event) => setNewSeriesPrice(event.target.value)} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>}
                                <div className="sm:col-span-2 grid gap-2 sm:grid-cols-[1fr_160px] items-center">
                                    <Label className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] p-3">Capítol de pagament (si el desactives, serà gratuït encara que la sèrie sigui premium) <Switch checked={isPremium} onCheckedChange={setIsPremium} /></Label>
                                    {isPremium && <Input type="number" min="0.5" step="0.01" value={price} onChange={(event) => setPrice(event.target.value)} placeholder="Preu (EUR)" className="bg-[color:var(--alb-ink-2)]" />}
                                </div>
                            </div>
                        )}
                        {uploadKind === 'trailer' && (
                            <div><Label>{t('upload.trailer_for')}</Label><Select value={trailerTarget} onValueChange={setTrailerTarget}><SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue placeholder={t('upload.trailer_select')} /></SelectTrigger><SelectContent>{series.map((item) => <SelectItem key={`series:${item.series_id}`} value={`series:${item.series_id}`}>{t('upload.series')} · {item.title}</SelectItem>)}{myVideos.filter((item) => !item.trailer_for_video_id && !item.trailer_for_series_id).map((item) => <SelectItem key={`video:${item.video_id}`} value={`video:${item.video_id}`}>{t('upload.video_prefix')} · {item.title}</SelectItem>)}</SelectContent></Select></div>
                        )}
                    </section>
                    <div>
                        <Label>{t('upload.file_label')}</Label>
                        <label htmlFor="file" className="mt-2 flex min-h-[220px] w-full flex-col items-center justify-center gap-2 overflow-hidden border-2 border-dashed border-[color:var(--alb-hairline)] rounded-lg cursor-pointer hover:bg-[color:var(--alb-surface-2)]">
                            {filePreview ? <video src={filePreview} className="aspect-video w-full bg-black object-contain" muted controls /> : <><Film className="h-8 w-8 text-[color:var(--alb-sun)]" /><span className="px-4 text-center text-sm text-[color:var(--alb-text-muted)]">{t('upload.file_pick')}</span></>}
                            {file && <span className="max-w-full truncate px-4 pb-3 text-xs text-[color:var(--alb-text-muted)]">{file.name}</span>}
                            <input data-testid="upload-file-input" id="file" type="file" accept="video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </label>
                    </div>
                    <div>
                        <Label>{t('upload.thumb_label')}</Label>
                        <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">Opcional: si no puges cap imatge, Albambam crearà automàticament una miniatura del vídeo.</p>
                        <div className="mt-2 grid gap-3 sm:grid-cols-[180px_1fr] sm:items-center">
                            <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded border border-[color:var(--alb-hairline)] bg-[color:var(--alb-ink-2)]">{thumbPreview ? <img src={thumbPreview} alt="Previsualització de la miniatura" className="h-full w-full object-cover" /> : <ImageIcon className="h-7 w-7 text-[color:var(--alb-text-muted)]" />}</div>
                            <Input data-testid="upload-thumb-input" type="file" accept="image/*" onChange={(e) => setThumb(e.target.files?.[0] || null)} className="min-w-0 bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                        </div>
                    </div>
                    <div>
                        <Label>{t('upload.video_title')}</Label>
                        <Input data-testid="upload-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                    </div>
                    <div>
                        <Label>{t('upload.description')}</Label>
                        <Textarea data-testid="upload-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label>{t('upload.category')} · {selectedCats.length}</Label>
                            <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto" data-testid="upload-category-picker">
                                {categories.map((c) => {
                                    const active = selectedCats.includes(c);
                                    return (
                                        <button key={c} type="button" onClick={() => toggleCategory(c)} className={`rounded-full border px-3 py-1 text-xs ${active ? 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] border-[color:var(--alb-sun)]' : 'border-[color:var(--alb-hairline)] text-[color:var(--alb-text-muted)]'}`}>{translateCategory(c, locale)}</button>
                                    );
                                })}
                            </div>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">Pots triar més d’una categoria; la primera serà la principal.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <div className="flex min-w-0 items-center justify-between gap-2 p-2 rounded-lg bg-[color:var(--alb-ink-2)] border border-[color:var(--alb-hairline)]">
                                <Switch data-testid="upload-is-short-switch" checked={uploadKind === 'trailer' || isShort} disabled={uploadKind === 'trailer'} onCheckedChange={setIsShort} />
                                <span className="text-sm">{t('upload.is_short')}</span>
                            </div>
                            {uploadKind === 'video' && (
                                <div className="flex min-w-0 items-center justify-between gap-2 p-2 rounded-lg bg-[color:var(--alb-ink-2)] border border-[color:var(--alb-hairline)]">
                                    <Switch data-testid="upload-is-premium-switch" checked={isPremium} onCheckedChange={setIsPremium} />
                                    <span className="text-sm">{t('upload.paid_video')}</span>
                                </div>
                            )}
                            {!safeMode && (
                            <div className="flex min-w-0 items-center justify-between gap-2 p-2 rounded-lg bg-[color:var(--alb-ink-2)] border border-[color:var(--alb-hairline)]">
                                <Switch data-testid="upload-is-adult-switch" checked={isAdult} disabled={uploadKind === 'episode' || uploadKind === 'trailer' || user.age < 18} onCheckedChange={setIsAdult} />
                                <span className="text-sm">{t('upload.adult_content')}</span>
                            </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <Label>{t('upload.video_language')}</Label>
                        <Select value={contentLanguage} onValueChange={setContentLanguage}>
                            <SelectTrigger className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {languageOptions.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {uploadKind === 'video' && isPremium && (
                        <div className="rounded-lg border border-[color:var(--alb-sun)]/50 bg-[color:var(--alb-surface-2)] p-4">
                            <Label htmlFor="upload-video-price">{t('upload.individual_price')}</Label>
                            <Input id="upload-video-price" data-testid="upload-video-price" value={price} onChange={(e) => setPrice(e.target.value)} type="number" step="0.01" min="0.5" required className="mt-2 bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                            <p className="mt-2 text-xs leading-5 text-[color:var(--alb-text-muted)]">{t('upload.paid_video_help')}</p>
                        </div>
                    )}
                    {!safeMode && isAdult && (
                        <div>
                            <Label>{t('upload.adult_category')} · {selectedAdultCats.length}</Label>
                            <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto" data-testid="upload-adult-category-picker">
                                {adultCategories.map((c) => {
                                    const active = selectedAdultCats.includes(c);
                                    return (
                                        <button key={c} type="button" onClick={() => toggleAdultCategory(c)} className={`rounded-full border px-3 py-1 text-xs ${active ? 'bg-[color:var(--alb-pink)] text-white border-[color:var(--alb-pink)]' : 'border-[color:var(--alb-hairline)] text-[color:var(--alb-text-muted)]'}`}>{translateCategory(c, locale)}</button>
                                    );
                                })}
                            </div>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">Pots triar més d’una categoria +18; la primera serà la principal.</p>
                        </div>
                    )}
                    {busy && <Progress value={progress} className="bg-[color:var(--alb-surface-2)]" />}
                    <Button data-testid="upload-submit-button" type="submit" disabled={busy} className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]"><UploadIcon className="h-4 w-4 mr-1" /> {busy ? t('upload.uploading', { n: progress }) : t('upload.submit')}</Button>
                </form>
            </Card>
        </main>
    );
}
